import './components/animated-route';

/**
 * External Dependencies
 */
import React, { Component } from 'react';
import axios from 'axios';
import thunk from 'redux-thunk';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import WebFont from 'webfontloader';
import { onError } from 'apollo-link-error';
import { RetryLink } from 'apollo-link-retry';
import { setContext } from '@apollo/client/link/context';
import { createUploadLink } from 'apollo-upload-client';
import { Provider, connect } from 'react-redux';
import { createStore, applyMiddleware } from 'redux';
import { withRouter, Route, Switch, BrowserRouter } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { ApolloProvider, ApolloClient, InMemoryCache, HttpLink, ApolloLink } from '@apollo/client';
import { KBarProvider } from 'kbar';
import { jitsuClient } from '@jitsu/sdk-js';

/**
 * Internal Dependencies
 */
import reducers from './reducers';
import {
  isSignPage,
  isPublicPage,
  isAdminPage,
  isExceptionPage,
  propsRedux,
  logger,
  buildMeUrl,
  readRegisterParams,
  stashRegisterParams,
  readStashedRegisterParams,
  clearStashedRegisterParams
} from './utils';
import { updateAuth as actionUpdateAuth } from './actions';
import { currentUser as updateCurrentUser, isHavingAccessToCreateProduct as updateAccess } from './actions/users';
import { fundraisingSettings as updateFundraisingSetting } from './actions/fundraisings';
import { linkTree as updateLinkTree } from './actions/linktree';
import { getFonts } from './assets/linktree-themes';
// import Chatwoot from './components/chatwoot';

import Routes from './Routes';
import PageYaybar from './components/page-yaybar';
import PageYaybarAdmin from './components/page-yaybar-admin';
import PageNavbar from './components/page-navbar';
import PageToasts from './components/page-toasts';
import Kbar from './components/kbar';
import { actions } from './components/kbar/config';
import { deleteAuthCookies, shouldStartWarmLogin } from './utils/auth';
import { beginLogin, markAuthenticated } from './utils/oidcAuth';
import AuthCallback from './pages/AuthCallback';
import LoadingAnimation from './components/loading-animation';

const createStoreWithMiddleware =
  !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
    ? applyMiddleware(thunk, logger, propsRedux)(createStore)
    : applyMiddleware(thunk, propsRedux)(createStore);

const $html = window.jQuery('html');
const $body = window.jQuery('body');

/**
 * Component PageWrap
 */
class PageWrap extends Component {
  constructor(props) {
    super(props);

    // Decided once, before the first render: a probe navigates the top window
    // away, so re-deciding it later would only ever race that navigation.
    this.state = { warmProbing: shouldStartWarmLogin() };
    this.unmounted = false;

    this.maybeCheckAuth = this.maybeCheckAuth.bind(this);
    this.maybeUpdateGlobalSettings = this.maybeUpdateGlobalSettings.bind(this);
    this.maybeScrollPageToTop = this.maybeScrollPageToTop.bind(this);
    this.intervalId = null;

    // Add a request interceptor
    axios.interceptors.request.use(config => {
      const token = Cookies.get('token');
      config.headers.Authorization = token ? `Bearer ${token}` : '';
      config.headers['Content-Type'] = 'application/json';
      return config;
    });

    // Add a response interceptor
    axios.interceptors.response.use(
      resp => {
        return resp;
      },
      err => {
        if (err && err.response && (419 === err.response.status || 401 === err.response.status)) {
          deleteAuthCookies();
        }

        return Promise.reject(err);
      }
    );
  }

  componentDidMount() {
    const { updateAuth } = this.props;
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const tokenPair = hash.get('token');
    const justAuthenticated = Boolean(tokenPair);
    // eslint-disable-next-line no-console
    console.warn('[App] componentDidMount path=%s hash=%s justAuth=%s tokenFromCookie=%s', window.location.pathname, window.location.hash, justAuthenticated, !!Cookies.get('token'));
    if (justAuthenticated) {
      updateAuth({ token: tokenPair });
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      window.location.reload();
      return;
    }

    // Register the service worker in production builds only. In development
    // the worker intercepts every navigation and hands back whatever the dev
    // server answered, which is exactly the kind of stale-shell confusion
    // BUG-16 was about. Existing registrations are deliberately left alone:
    // /sw.js is network-first and its only cache entry is offline.html, so
    // unregistering it would buy nothing and break the offline page.
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in global.navigator) {
      global.navigator.serviceWorker
        .register('/sw.js')
        .then(registration => {
          // eslint-disable-next-line no-console
          console.log('service worker registered: ', registration.scope);
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log('error register service worker: ', error);
        });
    }

    // Load all the fonts
    WebFont.load({ google: { families: getFonts() } });

    // Silent warm login (bug #2). Runs instead of the /sign-in bounce, not
    // alongside it: maybeCheckAuth and render both stand down while the probe is
    // in flight so a warm session lands in-app without flashing a login page.
    if (this.state.warmProbing) {
      this.startWarmLogin();
    }

    // On the tick we just applied a fresh token, this.props.auth.token is still
    // stale (undefined), so maybeCheckAuth() would bounce to /sign-in while /me
    // is still loading. componentDidUpdate re-runs it with the fresh token once
    // the updateAuth dispatch propagates to props.
    if (!justAuthenticated) {
      this.maybeCheckAuth();
    }
    this.maybeUpdateGlobalSettings();
    this.setCurrentUser();
    this.intervalId = setInterval(() => {
      const token = Cookies.get('token');
      if (token) {
        const parts = token.split(',');
        const decoded = jwtDecode(parts[0]);
        const now = Date.now() / 1000;
        if (now > decoded.exp - 60) {
          this.refreshUser();
        }
      }
    }, 30000);
  }

  componentDidUpdate(prevProps) {
    this.maybeCheckAuth(prevProps);
    this.maybeUpdateGlobalSettings(prevProps);
    this.maybeScrollPageToTop(prevProps);
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  // Ask auth-v2 whether this browser is already warm. beginLogin({silent:true})
  // builds the /authorize?prompt=none URL without navigating, so the top-window
  // bounce is ours to make. auth-v2 answers at /oidc/callback with either a code
  // (warm — AuthCallback exchanges it and hands back through #token=) or
  // error=login_required (cold — AuthCallback bounces to /sign-in?sso=none).
  startWarmLogin() {
    beginLogin({ silent: true })
      .then(url => {
        if (url) {
          window.location.replace(url);
          return;
        }
        // No URL means "stop asking": OIDC is unconfigured (missing env), or
        // MAX_SILENT_ATTEMPTS is spent because a warm token keeps coming back
        // unusable. This bail-out to the login screen IS the loop-breaker — a
        // login_required answer must never re-enter the probe.
        this.stopWarmLogin();
      })
      .catch(() => this.stopWarmLogin());
  }

  // Hand control back to the normal bootstrap: the setState re-runs
  // componentDidUpdate -> maybeCheckAuth, which now sees no token on a private
  // page and pushes /sign-in exactly as it did before this probe existed.
  stopWarmLogin() {
    if (this.unmounted) {
      return;
    }
    this.setState({ warmProbing: false });
  }

  maybeCheckAuth(prevProps) {
    const { auth, updateAuth, history } = this.props;

    // Hold the guard while the probe decides. Bouncing to /sign-in now would
    // flash the login page at a user who is about to be silently logged in, and
    // would let signin.jsx spend a second silent attempt on the same page load.
    if (this.state.warmProbing) {
      return;
    }
    const referrerUrl = auth.referrer && !isSignPage(auth.referrer) ? auth.referrer : '/';
    let newRedirect = false;

    // Redirect from Auth page for sub account.
    if (isSignPage() && auth.token && window.location.href.includes('/sign-in/sub/')) {
      deleteAuthCookies();
      newRedirect = window.location.pathname;
      // Redirect from Auth page.
    } else if (isSignPage() && auth.token && window.location.pathname.slice(0, 18) !== '/sign-in/referral/') {
      newRedirect = referrerUrl;

      // Redirect to Auth page.
    } else if (!isSignPage() && !auth.token && !isPublicPage()) {
      newRedirect = '/sign-in';

      // Check if use logged out or logged in.
    } else if (prevProps && auth.token !== prevProps.auth.token) {
      newRedirect = auth.token ? referrerUrl : '/sign-in';

      // Check if url has #
    } else if (window.location.hash) {
      newRedirect = window.location.hash.replace(/^#/g, '');
    }

    // Redirect.
    if (newRedirect) {
      // Everything below carries pathname/hash only, so a redirect drops
      // ?campaign= / ?ma= before refreshUser() ever gets to read them — the
      // signup attribution would be lost for good, because svc-proxy only
      // records it on the first /me of a brand new account. Stash it here;
      // refreshUser() reads it back and clears it once /me has taken it.
      stashRegisterParams(readRegisterParams(window.location.search));

      if (window.location.hash.replace(/^#/g, '').slice(0, 8) === '/sign-in' && !auth.token) {
        newRedirect = window.location.hash ? window.location.hash.replace(/^#/g, '') : window.location.pathname;
      } else {
        updateAuth({
          referrer: window.location.hash ? window.location.hash.replace(/^#/g, '') : window.location.pathname
        });
        history.push(newRedirect);
      }
    }
  }

  setCurrentUser() {
    this.refreshUser();
  }

  async refreshUser() {
    const { currentUser, linkTree, fundraisingSettings, isHavingAccessToCreateProduct, updateAuth } = this.props;
    let token = Cookies.get('token');

    if (token) {
      // The URL wins when it still has them; the stash covers the case where a
      // redirect (or a reload after one) already wiped the search string.
      const fromUrl = readRegisterParams(window.location.search);
      const stashed = readStashedRegisterParams();
      const registerReferral = fromUrl.registerReferral || stashed.registerReferral;
      const registerCoupon = fromUrl.registerCoupon || stashed.registerCoupon;

      try {
        try {
          // Refresh through /validate, NOT the OIDC /token refresh grant, even
          // though www is now an /authorize client. Both endpoints enforce the
          // same live-IdP-session binding, but /token verifies the refresh token
          // against `aud = mayar-www` while /validate checks the signature with
          // no aud constraint. www still holds aud-less Mayar pairs — every pair
          // already in the wild is one, and the /sign-in/{referral,campaign,sub}
          // links keep minting them through the legacy /oauth2/google flow — so
          // a /token refresh would reject them all. /validate is the one path
          // that refreshes both kinds.
          const refreshResponse = await axios.post(process.env.REACT_APP_REFRESH_URL, { withCredentials: true });
          if (refreshResponse?.data?.token) {
            token = refreshResponse.data.token;
            updateAuth({ token });
          }
        } catch (refreshErr) {
          const refreshStatus = refreshErr?.response?.status;
          if (refreshStatus === 401 || refreshStatus === 419) {
            deleteAuthCookies();
            return window.location.reload();
          }
          // Transient — keep the current session and continue.
        }

        const meResponse = await axios.get(
          buildMeUrl(process.env.REACT_APP_PROXY, { registerReferral, registerCoupon }),
          { withCredentials: true }
        );

        if (!meResponse || meResponse.status === 401) {
          deleteAuthCookies();
          return window.location.reload();
        }
        // The session is now proven acceptable by the API, not merely minted —
        // which is the only safe place to clear the warm-login retry counter.
        // MAX_SILENT_ATTEMPTS exists to cap a warm -> /me 401 -> reload loop, so
        // resetting it any earlier (e.g. the moment the cookie is written) would
        // make that loop infinite. See utils/oidcAuth markAuthenticated.
        markAuthenticated();
        // Attribution has now been handed to svc-proxy on a call it accepted, so
        // the stash has done its job. Dropping it any earlier would lose the
        // coupon to a reload mid-flow; keeping it any longer would attach it to
        // whoever logs in next in this tab.
        if (registerReferral || registerCoupon) {
          clearStashedRegisterParams();
        }
        if (meResponse.data.isMerchantModeDisabled) {
          window.localStorage.setItem(`account-mode:${meResponse.data.id}`, 'affiliator');
        }
        currentUser(meResponse.data);
        linkTree(meResponse.data.linkTree);
        fundraisingSettings(meResponse.data.fundraisingSettings);

        //init
        const jitsu = jitsuClient({
          key: process.env.REACT_APP_JITSU_KEY,
          tracking_host: 'https://t.jitsu.com'
        });

        //identify user
        jitsu.id({
          email: meResponse.data?.email,
          internal_id: meResponse.data?.id
        });

        //track page views
        jitsu.track('app_page');

        if (meResponse.data.role === 'admin') {
          const checkProductAccess = JSON.parse(meResponse.data.path)
            .filter(item => item.checked)
            .map(item => item.name)
            .includes('Buat / Edit Produk');

          if (!checkProductAccess) {
            isHavingAccessToCreateProduct(false);
          }
        }
      } catch {
        deleteAuthCookies();
        window.location.reload();
      }
    }
  }

  maybeUpdateGlobalSettings(prevProps) {
    const { settings } = this.props;

    // night mode.
    if (prevProps && prevProps.settings.night_mode !== settings.night_mode) {
      if (settings.night_mode) {
        $html.addClass('rui-night-mode');

        // eslint-disable-next-line no-unused-expressions
        import('./style-night.scss');
      } else {
        $html.removeClass('rui-night-mode');
      }
    }
    if (!prevProps && settings.night_mode) {
      $html.addClass('rui-night-mode');

      // eslint-disable-next-line no-unused-expressions
      import('./style-night.scss');
    }

    // spitlight mode.
    if (prevProps && prevProps.settings.spotlight_mode !== settings.spotlight_mode) {
      if (settings.spotlight_mode) {
        $body.addClass('rui-spotlightmode');
      } else {
        $body.removeClass('rui-spotlightmode');
      }
    }

    if (!prevProps && settings.spotlight_mode) {
      $body.addClass('rui-spotlightmode');
    }

    // section lines.
    if (prevProps && prevProps.settings.show_section_lines !== settings.show_section_lines) {
      if (settings.show_section_lines) {
        $body.addClass('rui-section-lines');
      } else {
        $body.removeClass('rui-section-lines');
      }
    }

    if (!prevProps && settings.show_section_lines) {
      $body.addClass('rui-section-lines');
    }

    // sidebar small.
    if (prevProps && prevProps.settings.sidebar_small !== settings.sidebar_small) {
      if (settings.sidebar_small) {
        $body.addClass('yay-hide');
      } else {
        $body.removeClass('yay-hide');
      }
    }

    if (!prevProps && settings.sidebar_small) {
      $body.addClass('yay-hide');
    }
  }

  maybeScrollPageToTop(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  render() {
    const { auth, location } = this.props;

    // Probe in flight: the top window is about to leave for /authorize. Render
    // the loader rather than the app so a warm user never sees the login page
    // they are being spared.
    if (this.state.warmProbing) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <LoadingAnimation mayar />
        </div>
      );
    }

    return (
      <TransitionGroup>
        <PageToasts />
        {isAdminPage() ? (
          <Route>{auth.token && <Route component={PageYaybarAdmin} />}</Route>
        ) : (
          <Route>
            {auth.token && window.location.pathname.slice(0, 18) !== '/sign-in/referral/' && !isExceptionPage() && (
              <>
                <Route component={PageNavbar} />
                <Route component={PageYaybar} />
              </>
            )}
          </Route>
        )}

        <CSSTransition key={location.pathname} timeout={300} classNames="rui-router-transition" unmountOnExit>
          <Routes location={location} />
        </CSSTransition>
      </TransitionGroup>
    );
  }
}

const PageWrapWithState = connect(
  ({ auth, settings, currentUser, linkTree, fundraisingSettings, isHavingAccessToCreateProduct }) => ({
    auth,
    settings,
    currentUser,
    linkTree,
    fundraisingSettings,
    isHavingAccessToCreateProduct
  }),
  {
    updateAuth: actionUpdateAuth,
    currentUser: updateCurrentUser,
    linkTree: updateLinkTree,
    fundraisingSettings: updateFundraisingSetting,
    isHavingAccessToCreateProduct: updateAccess
  }
)(withRouter(PageWrap));

/**
 * Component App
 */
class App extends Component {
  constructor(props) {
    super(props);

    // create redux store.
    this.store = createStoreWithMiddleware(
      reducers,
      window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__()
    );

    // Make store available globally for settings.jsx
    window.__REDUX_STORE__ = this.store;

    const multipleHttpLink2 = new RetryLink().split(
      operation => operation.getContext().upload,
      createUploadLink({ uri: process.env.REACT_APP_PROXY }),
      new HttpLink({ uri: `${process.env.REACT_APP_PROXY}` })
    );

    const retConfig = {
      delay: {
        initial: 400,
        max: Infinity,
        jitter: true
      },
      attempts: {
        max: 0
      }
    };

    const multipleHttpLink = new RetryLink(retConfig).split(
      operation => operation.getContext().v2 || operation.getContext().public,
      new HttpLink({ uri: `${process.env.REACT_APP_PROXY}` }),
      multipleHttpLink2
    );

    const httpLink = new RetryLink(retConfig).split(
      operation => {
        const token = Cookies.get('token');
        const customerToken = Cookies.get('customerToken')?.split(',')[0];
        if (operation.getContext().public) {
          operation.setContext({
            headers: {}
          });
        } else if (operation.getContext().v2) {
          if (!isPublicPage()) {
            if (!token) {
              return window.location.reload();
            }
          }

          const t = isPublicPage() ? customerToken : token;
          operation.setContext({
            headers: {
              authorization: t ? `Bearer ${t}` : ''
            }
          });
        } else if (operation.getContext().gographql) {
          return operation.getContext().gographql;
        } else {
          if (!token) {
            return window.location.reload();
          }

          operation.setContext({
            headers: {
              authorization: token ? `Bearer ${token}` : ''
            }
          });
        }
      },

      new HttpLink({ uri: `${process.env.REACT_APP_GOGRAPHQL_SERVER_URL}` }),
      multipleHttpLink
    );

    const authLink = setContext((_, { headers }) => {
      const token = Cookies.get('token');
      // get the authentication token from local cookie if it exists
      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : ''
        }
      };
    });

    const errorLink = onError(({ networkError }) => {
      // token expired and we need to renew the token by redirecting the browser to oauth page
      if (networkError && networkError.statusCode === 419) {
        deleteAuthCookies();
        window.location.reload();
      } else if (networkError && networkError.statusCode === 401) {
        // token no longer valid, force logout
        deleteAuthCookies();
        window.location.reload();
      }
    });

    this.client = new ApolloClient({
      cache: new InMemoryCache({
        // VERY IMPORTANT!!
        // we're using table with name=Subscription and it will causing issue on apollo when using ___typename for caching
        // I think it because apollo has reserved word 'Subscription'
        addTypename: false
      }),
      link: ApolloLink.from([errorLink, authLink, httpLink])
    });
  }

  render() {
    return (
      <ApolloProvider client={this.client}>
        <Provider store={this.store}>
          {/* {window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' && <Chatwoot />} */}

          <KBarProvider actions={actions}>
            <Kbar />
            <BrowserRouter>
              {/*
               * The OIDC warm-login callback lands at /oidc/callback?code=...&state=...
               * NOT /auth: traefik routes `/auth*` to the auth-gateway on the
               * deployed hosts, so a callback under /auth never reaches this SPA.
               * It must run its token exchange WITHOUT PageWrap's maybeCheckAuth
               * bouncing it to /sign-in first (/oidc/callback is neither a sign nor
               * a public page, so the bootstrap gate would redirect it while the
               * cookie is still empty). Rendering it on its own route keeps the
               * exchange isolated; on success it hands back to the normal
               * PageWrap bootstrap via a full-page #token= navigation.
               */}
              <Switch>
                <Route path="/oidc/callback" exact render={() => <AuthCallback />} />
                <Route render={() => <PageWrapWithState />} />
              </Switch>
            </BrowserRouter>
          </KBarProvider>
        </Provider>
      </ApolloProvider>
    );
  }
}

export default App;
